import { isEmbed, isReinvokeEmbed, getPathStationId } from 'store/websocketMiddleware';
import { isContinueAction } from 'cards/continueAt';

// tobin-order-at-seller: WHERE an Order click takes the buyer.
//
// The rule is one sentence: an Order click always lands the buyer in the
// SELLER's own station chat, because that is the only place where paying for
// the dish and asking about it ("what comes with it?", "can I pick up at 7?")
// are the same conversation. The ordering/payment station is plumbing — the
// buyer should never be dropped there as a destination.
//
// That leaves exactly one case where navigating would be wrong: the card is
// already rendering INSIDE the seller's chat (the restaurant's own station page,
// or a numeric /embed/s/<station_id> of it). Navigating there means a full page
// reload to the page you are already on. So that case dispatches the inline
// action in-thread instead — same landing spot, no reload.
//
// Everything else — the Tobin aggregator's search results, a Gallery tile, any
// other station's chat — gets the card's built-in Continue (`order_continue`):
// the seller's chat opens with this dish in context, and its `entry` (the offer
// id) fires the seller's declared `entry_action` (OrderingHandler.prepare_order)
// on arrival. A card from before `order_continue` falls back to the deep link
// (`/s/<seller>?entry=<offer_id>`), which lands in the same place without the dish.
//
// Never fire `order_action` away from the seller: its `station` is the ordering
// node, a thread the restaurant's own helpers cannot reach.

export const ORDER_INLINE = 'inline';   // dispatch in this thread (already at the seller)
export const ORDER_CONTINUE = 'continue'; // Continue at the seller's chat, starting the order
export const ORDER_LINK = 'link';       // navigate to the seller's chat (older cards)
export const ORDER_NONE = 'none';       // nothing to offer (no price ⇒ no offer minted)
export const RESERVE_LINK = 'reserve';  // Continue at the restaurant's chat to book

// Can this render dispatch an action at all? A re-invoke embed (Gallery tile)
// renders with no live thread behind it, so its Order must be a link even when
// the payload carries an action.
const canDispatchInline = (structured, onAction) =>
  !!(structured && structured.order_action && onAction && !isReinvokeEmbed());

// Is this card rendering inside the chat of the very restaurant it describes?
// Loose string compare: station ids arrive as a number from the card payload
// and as a number from the URL parse, but a stringified id from either side
// shouldn't silently mean "different station".
const atSeller = (structured) => {
  const seller = structured && structured.source_station_id;
  const here = getPathStationId();
  return seller != null && here != null && String(here) === String(seller);
};

export function pickOrderSurface(structured, onAction) {
  if (!structured) return ORDER_NONE;

  const alreadyAtSeller = atSeller(structured);

  if (alreadyAtSeller && canDispatchInline(structured, onAction)) return ORDER_INLINE;
  if (!alreadyAtSeller && isContinueAction(structured.order_continue)) return ORDER_CONTINUE;
  if (structured.order_url) return ORDER_LINK;
  // No deep link, and not at the seller. There IS possibly an order_action here
  // — but firing it would dispatch at the ordering node from a page that isn't
  // the seller's, which is precisely the stranding this module exists to
  // remove. The invariant wins over salvaging a button: an inline Order fires
  // ONLY when we're already at the seller (the branch above, which needs no
  // order_url). Unreachable with the current backend, since tobin_endpoints
  // sets all three order fields together inside one `if offer_id` — so the
  // choice is only about which impossible-today failure to prefer, and a
  // missing button beats a silently misrouted order. (!525 review.)
  return ORDER_NONE;
}

// tobin-reservations: WHICH affordance a dish card offers — Reserve or Order.
//
// A restaurant that takes bookings is somewhere you sit down, so the useful
// offer is a table; one that doesn't is somewhere you collect from, so the
// offer is the meal. Never both: from a listing a reader can't tell the two
// apart well enough to choose between them, and the card already knows which
// kind of place this is.
//
// `takes_reservations` comes from the restaurant's own `features` and rides on
// the card, so this is a pure read. Reserve is the card's built-in Continue
// action (`reserve_continue`): it opens the restaurant's chat with this dish.
//
// Two orderings matter:
//
//   1. Already at the restaurant ⇒ ORDER_INLINE still wins. Paying needs the
//      minted offer; booking does not — "a table for four Friday" typed into
//      the thread you are already in reaches `reserve` directly. A Reserve
//      LINK here would reload the page you are on to accomplish nothing, so
//      this returns no reserve affordance at the seller at all.
//   2. Everywhere else, Reserve outranks Order. Same call the third-party
//      consumer makes (tobin-web's `dishAction`), so a dish looks the same
//      wherever it is read.
export function pickDishAction(structured, onAction) {
  const order = pickOrderSurface(structured, onAction);
  // At the restaurant with a dispatchable order — see (1).
  if (order === ORDER_INLINE) return ORDER_INLINE;
  if (!structured || atSeller(structured)) return order;
  // Strict === true: a helper that ever sends the STRING "false" must not flip
  // the affordance, and a card from before this field existed (undefined) is
  // read as "no bookings" and falls through to Order.
  const bookable = structured.takes_reservations === true
    && isContinueAction(structured.reserve_continue);
  return bookable ? RESERVE_LINK : order;
}

// An embed lives in an iframe and must never navigate itself, so its link opens
// a new tab. In a normal chat the click is ordinary in-app navigation — the
// buyer is walking into the restaurant, and their previous thread resumes if
// they come back.
export const orderLinkTarget = () => (isEmbed() ? '_blank' : undefined);

